import { useState } from 'react';
import { useAuth } from '../lib/auth';
import * as pofApi from '../api/proofOfFunds';
import * as filesApi from '../api/files';
import { usePagedList } from '../lib/usePagedList';
import { useToast } from '../components/toast';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  PagedListFooter,
  StatusPill,
  formatDate
} from '../components/ui';
import { ui, cn } from '../lib/ui';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
const PAGE_SIZE = 25;

export default function ProofOfFunds() {
  const { isAccredited } = useAuth();
  const toast = useToast();

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [source, setSource] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const list = usePagedList(
    (cursor) =>
      pofApi
        .listMine({ limit: PAGE_SIZE, lastEvaluatedKey: cursor })
        .then((d) => ({ items: d.proofs || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    []
  );

  async function onSubmit(event) {
    event.preventDefault();
    setSubmitError(null);
    setBusy(true);
    try {
      // 1. Upload the document via the file service, 2. submit the proof
      // referencing the fileId. Documents are financial PII — handled and
      // audit-logged server-side.
      const uploaded = await filesApi.uploadFile(file);
      const fileId = (uploaded.file && uploaded.file.fileId) || uploaded.fileId;
      await pofApi.submitProof({
        documents: [{ fileId }],
        declaredAmount: Number(amount),
        currency,
        source: source || undefined
      });
      setAmount('');
      setSource('');
      setFile(null);
      toast.success('Proof of funds submitted for review.');
      list.reload();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className={ui.h1}>Accreditation verification</h1>
      {isAccredited && (
        <div className={ui.alertSuccess}>
          You are verified as an accredited investor.
        </div>
      )}

      <div className={cn(ui.grid, ui.gridMd2)}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Submit proof of funds</h3>
          <p className={cn(ui.muted, 'text-body-s')}>
            Upload a supporting document (bank or brokerage statement). A
            reviewer will assess it; approval unlocks accredited investor
            access. Your documents are treated as confidential financial
            information and every access is audit-logged.
          </p>
          <ErrorAlert error={submitError} />
          <form onSubmit={onSubmit}>
            <Field
              label="Declared amount"
              type="number"
              min="1"
              step="any"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <label className={ui.field}>
              <span className={ui.label}>Currency</span>
              <select
                className={ui.input}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <Field
              label="Source of funds (optional)"
              placeholder="e.g. business sale, savings"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
            <label className={ui.field}>
              <span className={ui.label}>Supporting document</span>
              <input
                className={ui.input}
                type="file"
                required
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
            </label>
            <button className={ui.btnPrimary} type="submit" disabled={busy || !file}>
              {busy ? 'Submitting…' : 'Submit for review'}
            </button>
          </form>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Your submissions</h3>
          <ErrorAlert error={list.error} />
          {list.loading ? (
            <Spinner />
          ) : list.items.length === 0 ? (
            !list.error && <EmptyState>No submissions yet.</EmptyState>
          ) : (
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((p) => (
                    <tr key={p.proofId}>
                      <td>{formatDate(p.submittedAt || p.createdAt)}</td>
                      <td>
                        {p.declaredAmount} {p.currency}
                      </td>
                      <td>
                        <StatusPill status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <PagedListFooter
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            endLabel={list.items.length ? 'No more submissions.' : null}
          />
        </section>
      </div>
    </>
  );
}
